import React, { useState } from "react";
import TreeView from "@mui/lab/TreeView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TreeItem from "@mui/lab/TreeItem";
import CopyIcon from "@atlaskit/icon/glyph/copy";


import { dateToMMDDYY, dateToMMDDYYmm, getDate } from "../../common/date_utils";
import { CancelInvitation } from "../../services/NetworkApi";
import { Button } from "../../UI/Button";


import { Grid } from "../../UI/Grid";
import useAuth from "../../routes/login/AuthContext";
import { useNetwork } from "../../hooks/useNetwork";
import { CancelInvitationModal } from "../CancelInvitationModal";
import { SendInvitationModal } from "../SendInvitationModal";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ResetInvitationButton } from "./ResetInvitationButton";
import { ModalProvider } from "../../contexts/ModalContext";
import { copyToClipboard } from "../../helpers/copyToClipboard";
import { generateQuickjoinLink } from "../../helpers/generateQuickjoinLink";
import { HelpTooltip } from "../HelpTooltip";

const RESEND_MODAL_TOOLTIP_TEXT = `This user has already registered and an invitation link is no longer required. This user can access their membership by signing in with the social account (Facebook, Google, Apple) listed above under "Login method`;

interface IProps {
  invitations: any;
  name: any;
  triggerReload: any;
  registered: boolean;
}

// Helper component for selectable data row inside the TreeItem content.
// We stop event propagation so MUI's TreeItem click handlers don't interfere
// with text selection/highlighting.
interface SelectableDataRowProps {
  label: string;
  value: string | number | null | undefined;
}

const SelectableDataRow: React.FC<SelectableDataRowProps> = ({ label, value }) => {
  const displayValue = value === null || value === undefined ? "null" : String(value);

  const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <div
      style={{ padding: "4px 0 4px 24px", userSelect: "text", cursor: "text" }}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onDoubleClick={stopPropagation}
    >
      {label}: {displayValue}
    </div>
  );
};

export const InvitationInformation: React.FC<IProps> = ({ invitations, name, triggerReload, registered }) => {
  const { showSuccessSnackbar } = useSnackbar();
  const { getL2 } = useNetwork();
  const [invitationIdToCancel, setInvitationIdToCancel] = React.useState<any>(null);
  const [resendModal, setResendModal] = useState({ show: false });
  const { isSuperAdmin, getAuthToken } = useAuth();

  const handleConfirmModalClose = () => setInvitationIdToCancel(null);

  const handleCancelInvitation = async () => {
    await CancelInvitation(invitationIdToCancel, await getAuthToken());
    triggerReload();
    handleConfirmModalClose();
  };

  const onCloseSendInvitationModal = () => setResendModal({ ...resendModal, show: false });

  const onSuccessInvitationModal = () => {
    onCloseSendInvitationModal();
    showSuccessSnackbar("Invitation sent!");
  };

	  const handleInvitationResend = (e: any) => {
	    e.stopPropagation();
	    setResendModal({ ...resendModal, show: true });
	  };

	  const handleCopy = (id: string) => async (e: any) => {
	    e.stopPropagation();
	    copyToClipboard(generateQuickjoinLink(id));
	    showSuccessSnackbar("Successfully copied to clipboard");
	  };

  const isInvitationEnabled = (l1id: number, l2Id: number) => {
    const mroundStatus = getL2(l1id, l2Id)?.matchingRoundStatus;
    return mroundStatus === "ACTIVE" || mroundStatus === "MATCHING";
  };

  return (
    <>
	      <TreeView
	        sx={{
	          py: 2,
	          '& *': {
	            userSelect: 'text !important',
	          },
	          '& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused': {
	            backgroundColor: 'transparent !important',
	          },
	        }}
	        disableSelection
	        aria-label="file system navigator"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        {invitations.map((invitation: any) => (
          <TreeItem
            key={invitation.id}
            nodeId={invitation.id}
            label={
              <Grid container spacing={2} alignItems="center">
                <Grid item xs style={{ userSelect: "text" }}>
                  Invitation Link: {invitation.id}
                </Grid>

                <Grid item>
                  <Button onClick={handleCopy(invitation.id)} iconBefore={<CopyIcon label="copy link" />} isDisabled={registered || !isInvitationEnabled(invitation.l1Id, invitation.l2Id)}>
                    Copy Invite Link
                  </Button>
                </Grid>
                
                <Grid item>
                  {
                    registered && (
                      <HelpTooltip text={RESEND_MODAL_TOOLTIP_TEXT}>
                      <Button appearance="primary" isDisabled>
                        Resend Invitation through RoomSync
                      </Button>
                      </HelpTooltip>
                    )
                  }
                  {
                  !registered && (
                      <Button appearance="primary" onClick={handleInvitationResend} isDisabled={!isInvitationEnabled(invitation.l1Id, invitation.l2Id)}>
                        Resend Invitation through RoomSync
                      </Button>
                    )
                  }
                  <ModalProvider>
                    <SendInvitationModal
                      name={name}
                      open={resendModal.show}
                      invitationId={invitation.id}
                      onClose={onCloseSendInvitationModal}
                      onSuccess={onSuccessInvitationModal}
                    />
                  </ModalProvider>
                </Grid>
                {isSuperAdmin() && (
                  <Grid item>
                    <ModalProvider>
                      <ResetInvitationButton name={name} invitationId={invitation.id}  isDisabled={!isInvitationEnabled(invitation.l1Id, invitation.l2Id)}/>
                    </ModalProvider>
                  </Grid>
                )}
              </Grid>
              
            }
	          >
	            <SelectableDataRow label="Invitation ID" value={invitation.id} />
	            <SelectableDataRow label="Invitation Link" value={generateQuickjoinLink(invitation.id)} />
	            <SelectableDataRow label="Invitation Type" value={invitation.invitationType} />
            <SelectableDataRow label="Created at" value={dateToMMDDYYmm(getDate(invitation.createdAt))} />
            <SelectableDataRow label="Updated at" value={dateToMMDDYYmm(getDate(invitation.updatedAt))} />
            <SelectableDataRow label="L1id" value={invitation.l1Id} />
            <SelectableDataRow label="L2id" value={invitation.l2Id} />
            <SelectableDataRow label="L3id" value={invitation.l3Id} />
            <SelectableDataRow label="L4id" value={invitation.l4Id} />
            <SelectableDataRow label="UID" value={invitation.uid} />
            <SelectableDataRow label="Claimed at" value={dateToMMDDYYmm(getDate(invitation.claimedAt))} />
            <SelectableDataRow label="Claimed by" value={invitation.claimedBy} />
            <SelectableDataRow label="Cancelled at" value={dateToMMDDYYmm(getDate(invitation.cancelledAt))} />
            <SelectableDataRow label="Expires at" value={dateToMMDDYY(getDate(invitation.expiresAt))} />
            {isSuperAdmin() && !invitation.cancelledAt && (
              <TreeItem
                nodeId={`${invitation}-13`}
                label={
                  <>
                    <Button appearance="link" onClick={() => setInvitationIdToCancel(invitation.id)}>
                      Cancel Invitation
                    </Button>
                    <CancelInvitationModal
                      show={!!invitationIdToCancel}
                      onClose={handleConfirmModalClose}
                      onConfirm={handleCancelInvitation}
                    />
                  </>
                }
              />
            )}
          </TreeItem>
        ))}
      </TreeView>
    </>
  );
};
